import { useCallback, useEffect, useState } from 'react'
import { useAuthenticationState } from '~/atoms/AuthenticationState'
import { useUserState } from '~/atoms/UserState'
import { isElectron } from '~/utils/common'

import { ChannelConfig } from '@mangovoice/types'

export type DownloadStage = 'checking' | 'downloading' | 'activating' | null

export interface ChannelSwitcherHookResult {
  currentChannel: string | null
  availableChannels: ChannelConfig[]
  isLoading: boolean
  error: string | null
  downloadStage: DownloadStage
  downloadProgress: number
  switchChannel: (channel: string) => Promise<void>
  switchChannelWithUpdate: (channel: string) => Promise<void>
  restartApp: () => void
  validateChannel: (
    channel: string,
  ) => Promise<{ exists: boolean; version: string | null }>
  getChannelVersion: (channel: string) => Promise<string | null>
}

export const useChannelSwitcher = (): ChannelSwitcherHookResult => {
  const [currentChannel, setCurrentChannel] = useState<string | null>(null)
  const [availableChannels, setAvailableChannels] = useState<ChannelConfig[]>(
    [],
  )
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [downloadStage, setDownloadStage] = useState<DownloadStage>(null)
  const [downloadProgress, setDownloadProgress] = useState<number>(0)
  const { isUserLoggedIn } = useAuthenticationState()
  const { isInternalUser } = useUserState()

  // Debug channel mismatch issues
  useEffect(() => {
    if (!isElectron || !isUserLoggedIn) return

    const checkChannelMismatch = async () => {
      try {
        const debugInfo = await window.electron?.channels.getDebugInfo()
        if (debugInfo && debugInfo.buildType !== debugInfo.currentChannel) {
          console.warn('🔄 Channel mismatch detected:', {
            buildType: debugInfo.buildType,
            currentChannel: debugInfo.currentChannel,
            lastSwitched: debugInfo.lastSwitched,
          })
        }
      } catch (err) {
        console.error('Failed to check channel debug info:', err)
      }
    }

    void checkChannelMismatch()
  }, [isElectron, isUserLoggedIn, currentChannel])

  const loadChannelData = useCallback(async () => {
    if (!isElectron || !isUserLoggedIn) return

    try {
      setIsLoading(true)
      setError(null)

      const [settingsChannel, buildChannel, available] = await Promise.all([
        window.electron?.channels.getCurrentChannel(),
        window.electron?.channels.getBuildEnv(),
        window.electron?.channels.getAvailableChannels(),
      ])

      // Use build channel (actual running version) as the current channel
      // This prevents UI showing wrong channel after restart
      setCurrentChannel(buildChannel || settingsChannel || null)
      setAvailableChannels(
        available
          ?.sort((a, b) => a.displayName.localeCompare(b.displayName))
          ?.filter((c: ChannelConfig) => !c.internalOnly || isInternalUser) ||
          [],
      )
    } catch (err) {
      console.error('Failed to load channel data:', err)
      setError(err instanceof Error ? err.message : 'Failed to load channels')
    } finally {
      setIsLoading(false)
    }
  }, [isElectron, isUserLoggedIn, isInternalUser])

  const switchChannel = useCallback(
    async (channel: string) => {
      if (!isElectron) return

      try {
        setIsLoading(true)
        setError(null)

        const result = await window.electron?.channels.switchChannel(channel)

        if (result?.success) {
          setCurrentChannel(channel)

          // Verify autoUpdater configuration after switch
          try {
            const status = await window.electron?.channels.getUpdaterStatus()
            console.log('✅ Channel switch verified:', {
              newChannel: channel,
              updaterStatus: status,
            })
          } catch (verifyErr) {
            console.warn('Could not verify autoUpdater status:', verifyErr)
          }

          // Always return for restart flow since channel manager returns needsRestart: true
          return
        } else {
          throw new Error('Failed to switch channel')
        }
      } catch (err) {
        console.error('Failed to switch channel:', err)
        setError(
          err instanceof Error ? err.message : 'Failed to switch channel',
        )
      } finally {
        setIsLoading(false)
      }
    },
    [isElectron],
  )

  const restartApp = useCallback(async () => {
    if (isElectron) {
      window.electron?.channels.restartApp()
    }
  }, [isElectron])

  const validateChannel = useCallback(
    async (channel: string) => {
      if (!isElectron) {
        return { exists: false, version: null }
      }

      try {
        const result = await window.electron?.channels.validateChannel(channel)
        return result || { exists: false, version: null }
      } catch (err) {
        console.error('Failed to validate channel:', err)
        return { exists: false, version: null }
      }
    },
    [isElectron],
  )

  const getChannelVersion = useCallback(
    async (channel: string): Promise<string | null> => {
      if (!isElectron) return null

      try {
        const version =
          await window.electron?.channels.getChannelVersion(channel)
        return version ?? null
      } catch (err) {
        console.error('Failed to get channel version:', err)
        return null
      }
    },
    [isElectron],
  )

  useEffect(() => {
    void loadChannelData()
  }, [loadChannelData])

  const switchChannelWithUpdate = useCallback(
    async (channel: string) => {
      if (!isElectron) return

      try {
        setIsLoading(true)
        setError(null)
        setDownloadStage('checking')
        setDownloadProgress(0)

        // Listen for update events
        window.electron?.receive('update-checking', () => {
          setDownloadStage('checking')
        })

        window.electron?.receive('update-available', () => {
          setDownloadStage('downloading')
          setDownloadProgress(0)
        })

        window.electron?.receive(
          'update-download-progress',
          (progress: any) => {
            setDownloadStage('downloading')
            setDownloadProgress(progress.percent || 0)
          },
        )

        window.electron?.receive('update-not-available', () => {
          setDownloadStage('activating')
        })

        const result =
          await window.electron?.channels.switchChannelWithUpdate(channel)

        if (result?.success) {
          // App will restart automatically, no need to update state
          return
        } else {
          throw new Error(result?.error || 'Failed to switch channel')
        }
      } catch (err) {
        console.error('Failed to switch channel with update:', err)
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to switch channel with update',
        )
        setDownloadStage(null)
      } finally {
        setIsLoading(false)
      }
    },
    [isElectron],
  )

  return {
    currentChannel,
    availableChannels,
    isLoading,
    error,
    downloadStage,
    downloadProgress,
    switchChannel,
    switchChannelWithUpdate,
    restartApp,
    validateChannel,
    getChannelVersion,
  }
}
